'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DistributorFormData {
  businessName: string;
  contactPerson: string;
  email: string;
  phone: string;
  location: string;
  experience: string;
  message: string;
}

interface DistributorFormErrors {
  businessName?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  location?: string;
  experience?: string;
  message?: string;
}

export default function DistributorFormSection() {
  const [formData, setFormData] = useState<DistributorFormData>({
    businessName: '',
    contactPerson: '',
    email: '',
    phone: '',
    location: '',
    experience: '',
    message: '',
  });

  const [errors, setErrors] = useState<DistributorFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const experienceLevels = [
    { id: 'exp_new', value: 'new', label: 'New to Distribution' },
    { id: 'exp_1-3', value: '1-3', label: '1-3 Years Experience' },
    { id: 'exp_3-5', value: '3-5', label: '3-5 Years Experience' },
    { id: 'exp_5plus', value: '5+', label: '5+ Years Experience' },
  ];

  const validateForm = (): boolean => {
    const newErrors: DistributorFormErrors = {};

    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }

    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9+\-\s()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.experience) {
      newErrors.experience = 'Please select your experience level';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Please tell us about your business';
    } else if (formData.message.trim().length < 20) {
      newErrors.message = 'Please provide more details (at least 20 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({
        businessName: '',
        contactPerson: '',
        email: '',
        phone: '',
        location: '',
        experience: '',
        message: '',
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof DistributorFormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <section id="distributor-form" className="py-section px-6 bg-secondary">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16 reveal">
          <p className="text-primary font-bold uppercase tracking-wider text-sm mb-4">Partnership Opportunities</p>
          <h2 className="text-heading-2 md:text-[3rem] lg:text-heading-1 leading-tight text-foreground mb-6">
            Become a <span className="gradient-text">Distributor</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Join our growing network of trusted distributors and bring premium Dutch-quality poultry nutrition to your community.
          </p>
        </div>

        {submitSuccess && (
          <div className="glass-card rounded-2xl p-6 mb-8 border-l-4 border-success reveal">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="CheckCircleIcon" size={24} className="text-success" />
              </div>
              <div>
                <h4 className="text-heading-4 text-foreground mb-1">Application Submitted Successfully!</h4>
                <p className="text-sm text-muted-foreground">
                  Thank you for your interest in becoming a Bloom Animal Feeds distributor. Our partnership team will review your application and contact you within 2-3 business days.
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-8 md:p-12 space-y-6 reveal">
          <div>
            <label htmlFor="businessName" className="block text-sm font-semibold text-foreground mb-2">
              Business Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              value={formData.businessName}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-xl border ${
                errors.businessName ? 'border-error' : 'border-input'
              } bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
              placeholder="Your business or agro-vet name"
            />
            {errors.businessName && (
              <p className="mt-2 text-sm text-error flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={16} />
                {errors.businessName}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="contactPerson" className="block text-sm font-semibold text-foreground mb-2">
              Contact Person <span className="text-error">*</span>
            </label>
            <input
              type="text"
              id="contactPerson"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-xl border ${
                errors.contactPerson ? 'border-error' : 'border-input'
              } bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
              placeholder="Full name of primary contact"
            />
            {errors.contactPerson && (
              <p className="mt-2 text-sm text-error flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={16} />
                {errors.contactPerson}
              </p>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                Email Address <span className="text-error">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-xl border ${
                  errors.email ? 'border-error' : 'border-input'
                } bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
                placeholder="business@example.com"
              />
              {errors.email && (
                <p className="mt-2 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-2">
                Phone Number <span className="text-error">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-xl border ${
                  errors.phone ? 'border-error' : 'border-input'
                } bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
                placeholder="0777 014 115"
              />
              {errors.phone && (
                <p className="mt-2 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.phone}
                </p>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="location" className="block text-sm font-semibold text-foreground mb-2">
                Location <span className="text-error">*</span>
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-xl border ${
                  errors.location ? 'border-error' : 'border-input'
                } bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
                placeholder="City, District, Uganda"
              />
              {errors.location && (
                <p className="mt-2 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.location}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="experience" className="block text-sm font-semibold text-foreground mb-2">
                Distribution Experience <span className="text-error">*</span>
              </label>
              <select
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-xl border ${
                  errors.experience ? 'border-error' : 'border-input'
                } bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
              >
                <option value="">Select experience level</option>
                {experienceLevels.map((level) => (
                  <option key={level.id} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
              {errors.experience && (
                <p className="mt-2 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.experience}
                </p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-foreground mb-2">
              Tell Us About Your Business <span className="text-error">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={6}
              className={`w-full px-4 py-3 rounded-xl border ${
                errors.message ? 'border-error' : 'border-input'
              } bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none`}
              placeholder="Tell us about your current business, distribution network, target market, and why you'd like to partner with Bloom Animal Feeds..."
            />
            {errors.message && (
              <p className="mt-2 text-sm text-error flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={16} />
                {errors.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full green-gradient text-white font-bold py-4 px-8 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                Submitting Application...
              </span>
            ) : (
              'Submit Distributor Application'
            )}
          </button>
        </form>
      </div>
    </section>
  );
}